import { usePermissions, useStore, useTranslate } from "ra-core";
import { Skeleton } from "@/components/ui/skeleton";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { DefaultReportsClientProvider } from "../reports/data/DefaultReportsClientProvider";
import { EmailAttentionBanner } from "../email/attention/EmailAttentionBanner"; // GAVION A12
import { useLiveRefresh, type Subscribe } from "./liveRefresh";
import { RepDashboard } from "./RepDashboard";
import { TeamDashboard } from "./TeamDashboard";

type View = "me" | "team";

/**
 * Reps always get the rep view; admins can switch to "Équipe" (spec 09 §4). The RPC re-checks the
 * team scope. `subscribe`: undefined = Supabase realtime (none in the demo), null = no live refresh.
 */
export const DashboardView = ({
  subscribe,
}: {
  subscribe?: Subscribe | null;
}) => {
  const translate = useTranslate();
  const { permissions, isPending } = usePermissions();
  const [view, setView] = useStore<View>("gavion.dashboard.view", "me");
  useLiveRefresh(subscribe);
  const isAdmin = permissions === "admin";
  const effective: View = isAdmin && view === "team" ? "team" : "me";
  return (
    <div className="space-y-6 max-md:px-4 max-md:py-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-extrabold">
          {translate("gavion.dashboard.title")}
        </h1>
        {isAdmin && (
          <ToggleGroup
            type="single"
            variant="outline"
            value={effective}
            onValueChange={(v) => v && setView(v as View)}
            aria-label={translate("gavion.dashboard.view_label")}
          >
            <ToggleGroupItem value="me" className="px-4">
              {translate("gavion.dashboard.view_me")}
            </ToggleGroupItem>
            <ToggleGroupItem value="team" className="px-4">
              {translate("gavion.dashboard.view_team")}
            </ToggleGroupItem>
          </ToggleGroup>
        )}
      </div>
      {/* GAVION A12: email sends on hold or to check (admins only) */}
      {isAdmin && <EmailAttentionBanner />}
      {/* Wait for the role so an admin on "Équipe" never flashes the rep view first. */}
      {isPending ? (
        <Skeleton className="h-40 w-full rounded-xl" />
      ) : effective === "team" ? (
        <TeamDashboard />
      ) : (
        <RepDashboard />
      )}
    </div>
  );
};

/** ra-core DashboardComponent replacing the upstream dashboard (spec 09 §4). */
export const GavionDashboard = () => (
  <DefaultReportsClientProvider>
    <DashboardView />
  </DefaultReportsClientProvider>
);
